import {useEffect, useState} from "react";
import {css} from "@emotion/react";

import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {head} from "@pg-mono/nodash";
import {SEARCH_FILTERS_AND_SORT_TEST_ID} from "@pg-mono/test-ids";
import {OfferType} from "@pg-rp/common-types";

import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {AreaFilter} from "../../../real_estate/components/filters/AreaFilter";
import {getAreaFilterLabelText} from "../../../real_estate/utils/filter_labels";
import {floorOptions, floorOptionsForHouse} from "../../../search/components/FloorChoicesSwitch";
import {getRoomLabel} from "../../../search/utils/room_field_data_translations";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {transformSortOptionToStringValue} from "../../detail/utils/transformSortOptionToStringValue";
import {transformStringValueToSortOption} from "../../detail/utils/transformStringValueToSortOption";
import {
    hitOfferModalFiltersRemove,
    trackFilterApplyForCategories,
    trackFilterClearForCategories,
    trackFilterOpenForCategory
} from "../../tracking/hit_offer_modal_filters";
import {FloorFilter} from "../offer_modal/FloorFilter";
import {OfferPropertiesSort} from "../offer_modal/OfferPropertiesSort";
import {RoomFilter} from "../offer_modal/RoomFilter";
import {SelectLabelWithEllipsis} from "../offer_modal/SelectLabelWithEllipsis";

interface IProps {
    propertyList: IPropertyListObj;
    offerType: OfferType;
}

export const MapOfferPropertyFilters = (props: IProps) => {
    const {propertyList, offerType} = props;
    const [sortValue, setSortValue] = useState<SortOptionsValues>(SortOptionsValues.NOT_SELECTED);

    const onSortOpen = () => {
        trackFilterOpenForCategory({eventName: "map_filters", filterCategory: "Sortuj"});
    };

    const onSortChange = (value?: SortOptionsValues) => {
        const isNewValueEmpty = !value;
        const sort = transformSortOptionToStringValue(value);
        const currentSort = propertyList.sort.currentSort || null;

        if (currentSort !== sort) {
            propertyList.onSortChange(isNewValueEmpty ? null : sort, propertyList.filters.currentFilters);
        }
    };

    const onSortApply = (value?: SortOptionsValues) => {
        const sortOptionLAbel = propertyListSortOptions.find((option) => option.value === value)?.label;
        trackFilterApplyForCategories({eventName: "map_filters", filterCategory: "Sortuj", customLabel: sortOptionLAbel});
        onSortChange(value);
    };

    const onSortClear = (isRemoveAction?: boolean) => {
        const sortOptionLAbel = propertyListSortOptions.find((option) => option.value === sortValue)?.label;
        (isRemoveAction ? hitOfferModalFiltersRemove : trackFilterClearForCategories)({
            eventName: "map_filters",
            filterCategory: "Sortuj",
            customLabel: sortOptionLAbel
        });
        onSortChange();
    };

    useEffect(() => {
        const sortNormalized = transformSortOptionToStringValue(sortValue);

        if (propertyList.sort.currentSort !== sortNormalized) {
            setSortValue(transformStringValueToSortOption(propertyList.sort.currentSort));
        }
    }, [propertyList.sort]);

    const initialValue =
        offerType === OfferType.FLAT
            ? (propertyList.filters.currentFilters.floor_choices as number[])
            : (propertyList.filters.currentFilters.house_storeys as number[]);

    const shouldShowFloorFilter = offerType === OfferType.FLAT || offerType === OfferType.HOUSE;

    return (
        <div css={filterWrapperStyle} data-testid={SEARCH_FILTERS_AND_SORT_TEST_ID.PROPERTY_LIST_FILTERS_BAR}>
            <RoomFilter
                css={barFilter}
                aplaCss={filterAplaStyle}
                initialValue={{
                    lower: +(propertyList.filters.currentFilters.rooms_0 as string) || "",
                    upper: +(propertyList.filters.currentFilters.rooms_1 as string) || ""
                }}
                labelRenderer={(value) => {
                    return (
                        <SelectLabelWithEllipsis label="Pokoje" subLabel={getRoomLabel(value, "", true)} showSubLabel={Boolean(value.lower || value.upper)} />
                    );
                }}
                onConfirm={(value) => {
                    hitUserSegmentRooms(value);

                    propertyList.onFiltersChange({
                        rooms_0: value.lower,
                        rooms_1: value.upper
                    });
                }}
                handleClear={() => {
                    propertyList.onFiltersChange({
                        rooms_0: undefined,
                        rooms_1: undefined
                    });
                }}
                trackingParams={{eventName: "map_filters"}}
            />

            <AreaFilter
                css={barFilter}
                aplaCss={filterAplaStyle}
                initialValue={{
                    lower: (propertyList.filters.currentFilters.area_0 as string) || "",
                    upper: (propertyList.filters.currentFilters.area_1 as string) || ""
                }}
                labelRenderer={(value) => {
                    const subLabel = `${getAreaFilterLabelText(value, "").text} m²`;
                    return <SelectLabelWithEllipsis label="Metraż" subLabel={subLabel} showSubLabel={Boolean(value.lower || value.upper)} />;
                }}
                onConfirm={(value) => {
                    propertyList.onFiltersChange({
                        area_0: value.lower,
                        area_1: value.upper
                    });
                }}
                handleClear={() => {
                    propertyList.onFiltersChange({
                        area_0: undefined,
                        area_1: undefined
                    });
                }}
                trackingParams={{eventName: "map_filters"}}
            />

            {shouldShowFloorFilter && (
                <FloorFilter
                    css={barFilter}
                    aplaCss={filterAplaStyle}
                    id="offer-modal-floors"
                    offerType={offerType}
                    initialValue={initialValue || []}
                    labelRenderer={(value) => {
                        const options = offerType === OfferType.HOUSE ? floorOptionsForHouse : floorOptions;
                        const floorChoiceVerbose = value && options.map((floor) => floor.value === head(value) && floor.label);
                        const subLabel = value ? (value.length === 1 ? floorChoiceVerbose : `+${value.length}`) : "";
                        return <SelectLabelWithEllipsis label="Piętra" subLabel={subLabel} showSubLabel={Boolean(value && value.length)} />;
                    }}
                    onConfirm={(value) => {
                        propertyList.onFiltersChange({
                            [offerType === OfferType.FLAT ? "floor_choices" : "house_storeys"]: value
                        });
                    }}
                    handleClear={() => {
                        propertyList.onFiltersChange({
                            [offerType === OfferType.FLAT ? "floor_choices" : "house_storeys"]: []
                        });
                    }}
                    trackingParams={{eventName: "map_filters"}}
                />
            )}

            <OfferPropertiesSort
                sortValue={sortValue}
                setSortValue={setSortValue}
                onConfirm={onSortApply}
                onClear={onSortClear}
                onRemove={() => onSortClear(true)}
                rightDropdown
                aplaCss={filterAplaStyle}
                onDropdownOpen={onSortOpen}
            />
        </div>
    );
};

const filterWrapperStyle = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${calculateRemSize(1.5)};
`;

const barFilter = css`
    flex: 1 1 33%;
    min-width: 0;
`;

const filterAplaStyle = css`
    border-radius: 0;
    position: absolute;
`;
import {useEffect, useState} from "react";
import {css} from "@emotion/react";

import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {head} from "@pg-mono/nodash";
import {SEARCH_FILTERS_AND_SORT_TEST_ID} from "@pg-mono/test-ids";
import {OfferType} from "@pg-rp/common-types";

import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {AreaFilter} from "../../../real_estate/components/filters/AreaFilter";
import {getAreaFilterLabelText} from "../../../real_estate/utils/filter_labels";
import {floorOptions, floorOptionsForHouse} from "../../../search/components/FloorChoicesSwitch";
import {getRoomLabel} from "../../../search/utils/room_field_data_translations";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {transformSortOptionToStringValue} from "../../detail/utils/transformSortOptionToStringValue";
import {transformStringValueToSortOption} from "../../detail/utils/transformStringValueToSortOption";
import {
    hitOfferModalFiltersRemove,
    trackFilterApplyForCategories,
    trackFilterClearForCategories,
    trackFilterOpenForCategory
} from "../../tracking/hit_offer_modal_filters";
import {FloorFilter} from "../offer_modal/FloorFilter";
import {OfferPropertiesSort} from "../offer_modal/OfferPropertiesSort";
import {RoomFilter} from "../offer_modal/RoomFilter";
import {SelectLabelWithEllipsis} from "../offer_modal/SelectLabelWithEllipsis";

interface IProps {
    propertyList: IPropertyListObj;
    offerType: OfferType;
}

export const MapOfferPropertyFilters = (props: IProps) => {
    const {propertyList, offerType} = props;
    const [sortValue, setSortValue] = useState<SortOptionsValues>(SortOptionsValues.NOT_SELECTED);

    const onSortOpen = () => {
        trackFilterOpenForCategory({eventName: "map_filters", filterCategory: "Sortuj"});
    };

    const onSortChange = (value?: SortOptionsValues) => {
        const isNewValueEmpty = !value;
        const sort = transformSortOptionToStringValue(value);
        const currentSort = propertyList.sort.currentSort || null;

        if (currentSort !== sort) {
            propertyList.onSortChange(isNewValueEmpty ? null : sort, propertyList.filters.currentFilters);
        }
    };

    const onSortApply = (value?: SortOptionsValues) => {
        const sortOptionLAbel = propertyListSortOptions.find((option) => option.value === value)?.label;
        trackFilterApplyForCategories({eventName: "map_filters", filterCategory: "Sortuj", customLabel: sortOptionLAbel});
        onSortChange(value);
    };

    const onSortClear = (isRemoveAction?: boolean) => {
        const sortOptionLAbel = propertyListSortOptions.find((option) => option.value === sortValue)?.label;
        (isRemoveAction ? hitOfferModalFiltersRemove : trackFilterClearForCategories)({
            eventName: "map_filters",
            filterCategory: "Sortuj",
            customLabel: sortOptionLAbel
        });
        onSortChange();
    };

    useEffect(() => {
        const sortNormalized = transformSortOptionToStringValue(sortValue);

        if (propertyList.sort.currentSort !== sortNormalized) {
            setSortValue(transformStringValueToSortOption(propertyList.sort.currentSort));
        }
    }, [propertyList.sort]);

    const initialValue =
        offerType === OfferType.FLAT
            ? (propertyList.filters.currentFilters.floor_choices as number[])
            : (propertyList.filters.currentFilters.house_storeys as number[]);

    const shouldShowFloorFilter = offerType === OfferType.FLAT || offerType === OfferType.HOUSE;

    return (
        <div css={filterWrapperStyle} data-testid={SEARCH_FILTERS_AND_SORT_TEST_ID.PROPERTY_LIST_FILTERS_BAR}>
            <RoomFilter
                css={barFilter}
                aplaCss={filterAplaStyle}
                initialValue={{
                    lower: +(propertyList.filters.currentFilters.rooms_0 as string) || "",
                    upper: +(propertyList.filters.currentFilters.rooms_1 as string) || ""
                }}
                labelRenderer={(value) => {
                    return (
                        <SelectLabelWithEllipsis label="Pokoje" subLabel={getRoomLabel(value, "", true)} showSubLabel={Boolean(value.lower || value.upper)} />
                    );
                }}
                onConfirm={(value) => {
                    hitUserSegmentRooms(value);

                    propertyList.onFiltersChange({
                        rooms_0: value.lower,
                        rooms_1: value.upper
                    });
                }}
                handleClear={() => {
                    propertyList.onFiltersChange({
                        rooms_0: undefined,
                        rooms_1: undefined
                    });
                }}
                trackingParams={{eventName: "map_filters"}}
            />

            <AreaFilter
                css={barFilter}
                aplaCss={filterAplaStyle}
                initialValue={{
                    lower: (propertyList.filters.currentFilters.area_0 as string) || "",
                    upper: (propertyList.filters.currentFilters.area_1 as string) || ""
                }}
                labelRenderer={(value) => {
                    const subLabel = `${getAreaFilterLabelText(value, "").text} m²`;
                    return <SelectLabelWithEllipsis label="Metraż" subLabel={subLabel} showSubLabel={Boolean(value.lower || value.upper)} />;
                }}
                onConfirm={(value) => {
                    propertyList.onFiltersChange({
                        area_0: value.lower,
                        area_1: value.upper
                    });
                }}
                handleClear={() => {
                    propertyList.onFiltersChange({
                        area_0: undefined,
                        area_1: undefined
                    });
                }}
                trackingParams={{eventName: "map_filters"}}
            />

            {shouldShowFloorFilter && (
                <FloorFilter
                    css={barFilter}
                    aplaCss={filterAplaStyle}
                    id="offer-modal-floors"
                    offerType={offerType}
                    initialValue={initialValue || []}
                    labelRenderer={(value) => {
                        const options = offerType === OfferType.HOUSE ? floorOptionsForHouse : floorOptions;
                        const floorChoiceVerbose = value && options.map((floor) => floor.value === head(value) && floor.label);
                        const subLabel = value ? (value.length === 1 ? floorChoiceVerbose : `+${value.length}`) : "";
                        return <SelectLabelWithEllipsis label="Piętra" subLabel={subLabel} showSubLabel={Boolean(value && value.length)} />;
                    }}
                    onConfirm={(value) => {
                        propertyList.onFiltersChange({
                            [offerType === OfferType.FLAT ? "floor_choices" : "house_storeys"]: value
                        });
                    }}
                    handleClear={() => {
                        propertyList.onFiltersChange({
                            [offerType === OfferType.FLAT ? "floor_choices" : "house_storeys"]: []
                        });
                    }}
                    trackingParams={{eventName: "map_filters"}}
                />
            )}

            <OfferPropertiesSort
                sortValue={sortValue}
                setSortValue={setSortValue}
                onConfirm={onSortApply}
                onClear={onSortClear}
                onRemove={() => onSortClear(true)}
                rightDropdown
                aplaCss={filterAplaStyle}
                onDropdownOpen={onSortOpen}
            />
        </div>
    );
};

const filterWrapperStyle = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${calculateRemSize(1.5)};
`;

const barFilter = css`
    flex: 1 1 33%;
    min-width: 0;
`;

const filterAplaStyle = css`
    border-radius: 0;
    position: absolute;
`;
